/**
 * Consolidación de líneas del packing list para el PDF comercial:
 * mismo contenedor + grado + producto + medida + pcs/paq → una fila.
 * La NF no separa filas; el peso del contenedor no se suma entre equivalentes.
 */
import { parseExcelNumber } from './parsePackingListExcel';
import { isRwNrPackingListRow } from './packingListRwNr';
import { sortPackingListDetailByGradeWithinContainer } from './packingListGradeSort';

function safe(s) {
  return s == null ? '' : String(s);
}

function detailContainerKey(r) {
  const c = safe(r?.container_celda).trim();
  if (c) return c;
  const excel = safe(r?._container_excel).trim();
  if (excel) return excel;
  return r?._container != null ? String(r._container) : '';
}

function normPdfKeyPart(v) {
  return safe(v).trim().toUpperCase().replace(/\s+/g, ' ');
}

function measurePdfKeyPart(v) {
  const n = parseExcelNumber(v);
  if (n != null) return String(n);
  return normPdfKeyPart(v);
}

function gradoPdfKey(r) {
  const g = normPdfKeyPart(r?.grado_celda);
  if (g) return g;
  if (isRwNrPackingListRow(r)) return 'RW';
  return '';
}

function pcsPdfKey(r) {
  const t = normPdfKeyPart(r?.piezas_por_paquete);
  if (t === '' || t === '0') return '0';
  return t;
}

function anchoPdfKey(r) {
  const w = measurePdfKeyPart(r?.ancho);
  if (w) return w;
  if (/\bRW\b/i.test(`${safe(r?.ancho)} ${safe(r?.grado_celda)} ${safe(r?.productos_texto)}`)) {
    return 'RW';
  }
  return '';
}

/**
 * Clave de consolidación PDF dentro de un contenedor:
 * grado + producto + medida + pcs/paq (la NF no participa).
 */
export function packingPdfLineMergeKey(r) {
  return [
    gradoPdfKey(r),
    normPdfKeyPart(r?.productos_texto),
    measurePdfKeyPart(r?.espesor),
    anchoPdfKey(r),
    measurePdfKeyPart(r?.largo),
    pcsPdfKey(r),
  ].join('|');
}

function firstFiniteNumber(...vals) {
  for (const v of vals) {
    if (v == null || v === '') continue;
    const n = Number(v);
    if (Number.isFinite(n)) return n;
  }
  return null;
}

function sumFiniteNumber(a, b) {
  const na = a != null && a !== '' && Number.isFinite(Number(a)) ? Number(a) : null;
  const nb = b != null && b !== '' && Number.isFinite(Number(b)) ? Number(b) : null;
  if (na == null && nb == null) return null;
  return (na || 0) + (nb || 0);
}

function mergeContractLabels(a, b) {
  const parts = [];
  const seen = new Set();
  for (const raw of [a, b]) {
    const t = safe(raw).trim();
    if (!t) continue;
    const k = t.toUpperCase();
    if (seen.has(k)) continue;
    seen.add(k);
    parts.push(t);
  }
  return parts.join('; ');
}

function startConsolidatedPdfRow(r) {
  return {
    ...r,
    cantidad_paquetes: firstFiniteNumber(r.cantidad_paquetes),
    total_piezas: firstFiniteNumber(r.total_piezas),
    total_m3: firstFiniteNumber(r.total_m3),
    peso: firstFiniteNumber(r.peso),
    peso_neto: firstFiniteNumber(r.peso_neto),
    nf_celda: safe(r.nf_celda).trim(),
  };
}

function mergeConsolidatedPdfRow(into, r) {
  into.cantidad_paquetes = sumFiniteNumber(into.cantidad_paquetes, r.cantidad_paquetes);
  into.total_piezas = sumFiniteNumber(into.total_piezas, r.total_piezas);
  into.total_m3 = sumFiniteNumber(into.total_m3, r.total_m3);
  if (into.peso == null) into.peso = firstFiniteNumber(r.peso);
  if (into.peso_neto == null) into.peso_neto = firstFiniteNumber(r.peso_neto);
  into.nf_celda = mergeContractLabels(into.nf_celda, r.nf_celda);
  if (!safe(into.productos_texto).trim() && safe(r.productos_texto).trim()) {
    into.productos_texto = r.productos_texto;
  }
  if (!safe(into.grado_celda).trim() && safe(r.grado_celda).trim()) {
    into.grado_celda = r.grado_celda;
  }
  return into;
}

/**
 * Consolida líneas del detalle para el PDF del packing list.
 */
export function consolidatePackingDetailForPdf(detalleExcel) {
  const rows = sortPackingListDetailByGradeWithinContainer(detalleExcel || []);
  if (rows.length <= 1) return rows.map((r) => ({ ...r }));

  const containerOrder = [];
  const byContainer = new Map();

  rows.forEach((r, idx) => {
    const ck = detailContainerKey(r) || `__row_${idx}`;
    if (!byContainer.has(ck)) {
      byContainer.set(ck, new Map());
      containerOrder.push(ck);
    }
    const groups = byContainer.get(ck);
    const lineKey = packingPdfLineMergeKey(r);
    if (!groups.has(lineKey)) {
      groups.set(lineKey, startConsolidatedPdfRow(r));
    } else {
      mergeConsolidatedPdfRow(groups.get(lineKey), r);
    }
  });

  const out = [];
  for (const ck of containerOrder) {
    for (const agg of byContainer.get(ck).values()) {
      out.push(agg);
    }
  }
  return out;
}
